import { useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useState } from "react";

// Define a debounce function with TypeScript types
function debounce<T extends (...args: any[]) => void>(func: T, wait: number) {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Define the return type of the hook
interface MediaQueryResults {
  isMobile: boolean;
  isTablet: boolean;
  isMobileOrTablet: boolean;
  isDesktop: boolean;
  isTabletOrDesktop: boolean;
  windowWidth: number;
}

const useMediaQueries = (): MediaQueryResults => {
  const theme = useTheme();
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);

  const isMobile: boolean = useMediaQuery(theme.breakpoints.down("md")); // 0px - 899px
  const isTablet: boolean = useMediaQuery(
    theme.breakpoints.between("md", "lg") // 900 - 1199px
  );
  const isDesktop: boolean = useMediaQuery(theme.breakpoints.up("lg")); // 1200px and above
  const isMobileOrTablet: boolean = useMediaQuery(theme.breakpoints.down("lg")); // 0px - 1199px
  const isTabletOrDesktop: boolean = useMediaQuery(theme.breakpoints.up("md")); // 900px and above

  // Effect to update window width on resize with debouncing
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    const debouncedHandleResize = debounce(handleResize, 100); // Adjust debounce time as needed

    window.addEventListener("resize", debouncedHandleResize);
    return () => {
      window.removeEventListener("resize", debouncedHandleResize);
    };
  }, []);

  return {
    isMobile,
    isTablet,
    isMobileOrTablet,
    isDesktop,
    isTabletOrDesktop,
    windowWidth,
  };
};

export default useMediaQueries;
